import { apmBase } from "@elastic/apm-rum";
import { ApmVuePlugin } from "@elastic/apm-rum-vue";
import type { App } from "vue";
import type { Router } from "vue-router";

/**
 * Hold span used to keep a Matcher route-change transaction open while
 * slug-content.vue resolves the objectType asynchronously.
 *
 * Problem: ApmVuePlugin's afterEach schedules detectFinish via requestAnimationFrame
 * (~16 ms). The slug API call takes 50–500 ms, so without intervention the transaction
 * is sent as bare "Matcher" before setMatchingRouteName can rename it to
 * "Matcher/Category", "Matcher/Product", etc.
 *
 * Solution: start a span in afterEach for Matcher routes. APM will not close a
 * transaction while it has open spans. renameMatcherTransaction ends this span after
 * renaming, allowing detectFinish to complete the transaction with the correct name.
 */
let matcherHoldSpan: { end(): void } | null = null;

/** Called by renameMatcherTransaction (and on every new navigation) to release the hold. */
export function endMatcherHoldSpan(): void {
  if (matcherHoldSpan) {
    matcherHoldSpan.end();
    matcherHoldSpan = null;
  }
}

/**
 * Registers the Elastic APM Vue plugin and route-name override hooks.
 *
 * MUST be called BEFORE app.use(router) so the plugin's beforeEach/afterEach hooks
 * are in place for the initial navigation.
 */
export function setupApmVuePlugin(app: App, router: Router): void {
  if (import.meta.env.APP_APM_ENABLED !== "true") return;

  // Register beforeEach BEFORE ApmVuePlugin so our cleanup fires first, releasing
  // any hold span from the previous Matcher navigation before ApmVuePlugin starts
  // a new transaction for the incoming route.
  //
  // Also eagerly rename the page-load transaction using the destination route name.
  // APM creates the page-load transaction at initApm() time with a URL-based name
  // (e.g. "/primary-ingredients", "/:id" for numeric slugs). If detectFinish fires
  // before our afterEach runs (all resources cached → fast load), the transaction
  // would be sent with the raw URL name. setInitialPageLoadName overwrites it before
  // that can happen. It is a no-op when there is no active page-load transaction
  // (APM internally guards: only renames if tr.type === "page-load").
  router.beforeEach((to) => {
    endMatcherHoldSpan();
    // For Matcher routes use the URL path; for named routes use the route name.
    const name = to.name === "Matcher" ? to.path : (typeof to.name === "string" ? to.name : to.path);
    apmBase.setInitialPageLoadName(name);
  });

  app.use(ApmVuePlugin, { router });

  // afterEach runs after ApmVuePlugin's afterEach (FIFO order), so detectFinish is
  // already scheduled when we set the name and (for Matcher) open the hold span.
  router.afterEach((to) => {
    const tx = apmBase.getCurrentTransaction();
    if (!tx) return;

    // For Matcher (slug) routes use the URL path so the APM transaction name matches
    // what analytics records — enabling direct correlation by URL.
    // For all other named routes use the route name ("Dashboard", "Orders", etc.).
    tx.name = to.name === "Matcher" ? to.path : ((to.name as string) || to.path);

    // For Matcher routes, hold the transaction open until the slug API resolves
    // and labelMatcherTransaction is called to add the content-type label.
    if (to.name === "Matcher") {
      const span = tx.startSpan("Matcher: resolving slug", "app");
      matcherHoldSpan = span ?? null;
    }
  });
}
